import { useEffect, useState } from "react";
import { X, Plus, Trash2, Bell, Loader2, Pencil } from "lucide-react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";

// ---------------- Player-facing popup (mounted in the game shell only) ----------------
export const GamePopup = () => {
  const [popup, setPopup] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => gameApi.get("/game/popups/active").then((r) => setPopup(r.data.popup)).catch(() => setPopup(null));
  useEffect(() => { load(); }, []);

  const dismiss = async () => {
    if (!popup) return;
    setBusy(true);
    try {
      await gameApi.post(`/game/popups/${popup.id}/dismiss`);
      load();   // show the next waiting popup (oldest first), if any
    } catch { setPopup(null); } finally { setBusy(false); }
  };

  if (!popup) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="game-popup">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-6 pb-3 pt-5">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-secondary/15 text-secondary"><Bell size={18} /></span>
            <h3 className="font-display text-xl font-bold text-primary" data-testid="game-popup-title">{popup.title}</h3>
          </div>
          <button data-testid="game-popup-x" onClick={dismiss} disabled={busy} className="rounded-full p-1 text-muted-foreground hover:bg-muted active:scale-90"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/85" data-testid="game-popup-body">{popup.body}</p>
        </div>
        <div className="flex shrink-0 flex-col gap-2 border-t border-border px-6 pb-5 pt-3">
          {popup.button_url && popup.button_label && (
            <a data-testid="game-popup-link" href={popup.button_url} target="_blank" rel="noopener noreferrer" onClick={dismiss}
              className="flex h-11 items-center justify-center rounded-xl bg-secondary font-bold text-secondary-foreground active:scale-95">{popup.button_label}</a>
          )}
          <button data-testid="game-popup-gotit" onClick={dismiss} disabled={busy}
            className="h-11 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">{busy ? "…" : "Got It"}</button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Admin management section ----------------
const BLANK = { title: "", body: "", button_label: "", button_url: "", mode: "now", scheduled_at: "" };
const STATUS_STYLE = { active: "bg-emerald-100 text-emerald-700", scheduled: "bg-amber-100 text-amber-700", inactive: "bg-muted text-muted-foreground" };

export const PopupAdmin = () => {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => gameApi.get("/game/admin/popups").then((r) => setList(r.data.popups)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(BLANK); setEditId(null); };

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and message are required."); return; }
    if (form.mode === "schedule" && !form.scheduled_at) { toast.error("Pick a date & time to schedule."); return; }
    setBusy(true);
    const payload = {
      title: form.title, body: form.body, button_label: form.button_label, button_url: form.button_url,
      mode: form.mode, scheduled_at: form.mode === "schedule" ? new Date(form.scheduled_at).toISOString() : null,
    };
    try {
      if (editId) await gameApi.put(`/game/admin/popups/${editId}`, payload);
      else await gameApi.post("/game/admin/popups", payload);
      toast.success(editId ? "Popup updated" : "Popup published");
      reset(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save popup"); } finally { setBusy(false); }
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      title: p.title, body: p.body, button_label: p.button_label || "", button_url: p.button_url || "",
      mode: p.scheduled_at ? "schedule" : "now",
      scheduled_at: p.scheduled_at ? new Date(p.scheduled_at).toISOString().slice(0, 16) : "",
    });
  };

  const toggleActive = async (p) => {
    try { await gameApi.put(`/game/admin/popups/${p.id}`, { active: !p.active, mode: p.scheduled_at ? "schedule" : "now", scheduled_at: p.scheduled_at }); load(); }
    catch { toast.error("Could not update"); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try { await gameApi.delete(`/game/admin/popups/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="mt-3 space-y-4" data-testid="admin-popups">
      <div className="rounded-xl border border-secondary/40 bg-secondary/5 p-3">
        <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-primary">{editId ? <Pencil size={15} /> : <Plus size={15} />} {editId ? "Edit Popup" : "New Popup"}</p>
        <input data-testid="popup-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" maxLength={80}
          className="mb-2 h-10 w-full rounded-lg border border-border bg-background px-3 text-sm" />
        <textarea data-testid="popup-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Message / body text" rows={3}
          className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        <div className="mb-2 grid grid-cols-2 gap-2">
          <input data-testid="popup-btn-label" value={form.button_label} onChange={(e) => setForm({ ...form, button_label: e.target.value })} placeholder="Button label (optional)" className="h-10 rounded-lg border border-border bg-background px-3 text-sm" />
          <input data-testid="popup-btn-url" value={form.button_url} onChange={(e) => setForm({ ...form, button_url: e.target.value })} placeholder="Button link URL (optional)" className="h-10 rounded-lg border border-border bg-background px-3 text-sm" />
        </div>
        <div className="mb-2 flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5"><input data-testid="popup-mode-now" type="radio" checked={form.mode === "now"} onChange={() => setForm({ ...form, mode: "now" })} /> Show immediately</label>
          <label className="flex items-center gap-1.5"><input data-testid="popup-mode-schedule" type="radio" checked={form.mode === "schedule"} onChange={() => setForm({ ...form, mode: "schedule" })} /> Schedule</label>
          {form.mode === "schedule" && (
            <input data-testid="popup-schedule-at" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="h-9 rounded-lg border border-border bg-background px-2 text-sm" />
          )}
        </div>
        <div className="flex gap-2">
          <button data-testid="popup-submit" disabled={busy} onClick={submit} className="h-10 flex-1 rounded-lg bg-primary text-sm font-bold text-primary-foreground disabled:opacity-50">{busy ? "…" : editId ? "Save Changes" : "Publish Popup"}</button>
          {editId && <button data-testid="popup-cancel-edit" onClick={reset} className="h-10 rounded-lg border border-border px-4 text-sm font-bold text-primary">Cancel</button>}
        </div>
      </div>

      <div className="space-y-2">
        {!list ? <Loading /> : list.length === 0 ? <p className="text-center text-sm text-muted-foreground">No popups yet.</p>
          : list.map((p) => (
            <div key={p.id} data-testid={`popup-row-${p.id}`} className="rounded-xl border border-border bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                    <h4 className="truncate font-bold text-primary">{p.title}</h4>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.body}</p>
                  <p className="mt-1 text-[10px] text-muted-foreground">
                    {p.scheduled_at ? `Scheduled ${new Date(p.scheduled_at).toLocaleString()}` : `Created ${new Date(p.created_at).toLocaleDateString()}`} · {p.dismiss_count} dismissed
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <button data-testid={`popup-edit-${p.id}`} onClick={() => startEdit(p)} className="rounded-lg p-1.5 text-secondary hover:bg-muted"><Pencil size={15} /></button>
                  <button data-testid={`popup-delete-${p.id}`} onClick={() => del(p)} className="rounded-lg p-1.5 text-red-500 hover:bg-muted"><Trash2 size={15} /></button>
                </div>
              </div>
              {p.status !== "inactive" ? (
                <button data-testid={`popup-deactivate-${p.id}`} onClick={() => toggleActive(p)} className="mt-2 h-8 w-full rounded-lg border border-border text-xs font-bold text-primary">Deactivate</button>
              ) : (
                <button data-testid={`popup-reactivate-${p.id}`} onClick={() => toggleActive(p)} className="mt-2 h-8 w-full rounded-lg bg-emerald-600 text-xs font-bold text-white">Reactivate</button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

const Loading = () => <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</p>;
